import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { CLIPBOARD_SCORE_FULL_PATH } from "@src/appV2/redesign/ClipboardScore/utils/paths";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ListLoadingState } from "@src/appV2/redesign/components/ListLoadingState";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useGetWorkerPaymentsStats } from "@src/appV2/redesign/Payments/Earnings/api/useGetWorkerPaymentsStats";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ShareScoreCard } from "./ShareScoreCard";
import { ShareScoreHeader } from "./ShareScoreHeader";
import { type ShareScoreEntryPoint, type ShareScoreEventPayload } from "./types";
import { useImageAsDataUrl } from "./useImageAsDataUrl";
import { useShareClipboardScore } from "./useShareClipboardScore";

interface LocationState {
  entryPoint?: ShareScoreEntryPoint;
}

export function ShareScorePage() {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const entryPoint: ShareScoreEntryPoint = location.state?.entryPoint ?? "clipboard-score-page";

  const isShareClipboardScoreEnabled = useCbhFlag(CbhFeatureFlag.SHARE_CLIPBOARD_SCORE, {
    defaultValue: false,
  });

  const worker = useDefinedWorker();
  const { userId, name, address, attendance } = worker;

  const { data: workerProfilePicture, isInitialLoading: isLoadingProfileImage } =
    useWorkerProfilePicture(userId);
  const profileImageUrl = workerProfilePicture?.distribution_url;
  const profileImageDataUrl = useImageAsDataUrl(profileImageUrl);

  const { data: clipboardScoreProfile, isLoading: isScoreLoading } = useGetClipboardScoreProfile(
    userId,
    {
      enabled: isDefined(userId),
    }
  );

  const { data: paymentsStats, isLoading: isPaymentsLoading } = useGetWorkerPaymentsStats();

  // "Workers" is the API's catch-all bucket for workers with no specific qualification
  const qualification = clipboardScoreProfile?.clipboardScoreRank?.qualification;
  const { data: qualificationLabels } = useGetQualificationDisplayLabels(
    isDefined(qualification) ? [qualification] : []
  );
  const qualificationDisplayLabel =
    qualificationLabels?.[0] === "Workers" ? undefined : qualificationLabels?.[0];

  const clipboardScore = clipboardScoreProfile?.clipboardScore ?? 0;
  const rank = clipboardScoreProfile?.clipboardScoreRank?.rank;
  const outOf = clipboardScoreProfile?.clipboardScoreRank?.outOf;
  const shiftsWorked = attendance?.completedShifts ?? 0;
  const totalEarningsInCents = paymentsStats?.data.attributes.totalEarningsInCents ?? 0;

  const cityState = isDefined(address) ? `${address.city}, ${address.stateCode}` : "";

  const eventPayload: ShareScoreEventPayload = useMemo(
    () => ({
      workerId: userId,
      clipboardScore,
      rank,
      outOf,
      totalEarningsInCents,
      shiftsWorked,
      entryPoint,
    }),
    [userId, clipboardScore, rank, outOf, totalEarningsInCents, shiftsWorked, entryPoint]
  );

  const { mutate: shareScore, isLoading: isSharing } = useShareClipboardScore({
    eventPayload,
  });

  useLogEffect(APP_V2_USER_EVENTS.SHARE_CLIPBOARD_SCORE_PAGE_VIEWED, eventPayload, {
    enabled: isShareClipboardScoreEnabled && !isScoreLoading && !isPaymentsLoading,
  });

  useEffect(() => {
    if (!isShareClipboardScoreEnabled) {
      history.replace(CLIPBOARD_SCORE_FULL_PATH);
    }
  }, [isShareClipboardScoreEnabled, history]);

  if (!isShareClipboardScoreEnabled) {
    return null;
  }

  if (isScoreLoading || isPaymentsLoading) {
    return <ListLoadingState texts={["Loading your score..."]} />;
  }

  return (
    <PageWrapper
      variant="tertiary"
      sx={{
        display: "flex",
        flexDirection: "column",
        background:
          // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
          "linear-gradient(180deg, rgba(218, 241, 232, 1) 0%, rgba(251, 242, 232, 1) 100%)",
      }}
    >
      <ShareScoreHeader />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          paddingX: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 360 }}>
          <ShareScoreCard
            profileImageUrl={profileImageUrl}
            isLoadingProfileImage={isLoadingProfileImage}
            fullName={name}
            qualification={qualificationDisplayLabel}
            cityState={cityState}
            clipboardScore={clipboardScore}
            rank={rank ?? 0}
            outOf={outOf ?? 0}
            shiftsWorked={shiftsWorked}
            totalEarningsInCents={totalEarningsInCents}
          />
        </Box>
      </Box>

      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={
            isSharing ||
            isLoadingProfileImage ||
            (isDefined(profileImageUrl) && !isDefined(profileImageDataUrl))
          }
          onClick={() => {
            shareScore();
          }}
        >
          Share
        </Button>
      </DialogFooter>

      {/* Off-screen element captured for share image via domToPng.
          Uses position:fixed so it doesn't extend the scrollable area. */}
      <Box
        sx={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: "100%",
          height: "65%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <ShareScoreCard
          id="share-clipboard-score-card"
          profileImageUrl={profileImageDataUrl}
          fullName={name}
          qualification={qualificationDisplayLabel}
          cityState={cityState}
          clipboardScore={clipboardScore}
          rank={rank ?? 0}
          outOf={outOf ?? 0}
          shiftsWorked={shiftsWorked}
          totalEarningsInCents={totalEarningsInCents}
        />
      </Box>
    </PageWrapper>
  );
}
